import { createContext, useContext, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { setPreviewTier } from "../data/client";
import css from "./preview.module.css";

/**
 * Preview mode — `specs/patron-preview/`.
 *
 * The tier lives in `?as=`, so a reload keeps the preview and the URL is the
 * state rather than a copy of it.
 *
 * **The URL and the header are deliberately different things.** The address bar
 * says what the *screen* is showing; the header says what the *request* is
 * asking for. Nothing forwards one to the other automatically — this component
 * is the only place they meet, which is what stops a future route from passing
 * a query parameter through to the API and turning a display hint into an
 * authorization input.
 *
 * `?as=` grants nothing on its own: the server honours the header only for the
 * verified owner and ignores it otherwise.
 */

type PreviewState = {
  /** The previewed tier id, or null when this is an ordinary session. */
  tier: string | null;
  previewing: boolean;
  /** Switch tier in place; the library re-fetches as that tier. */
  setTier: (tierId: string) => void;
  /** Leave preview and return to where Studio was left. */
  exit: () => void;
};

const Context = createContext<PreviewState | null>(null);

/** Where to return on EXIT TO STUDIO. Session-scoped: it is a back button. */
const RETURN_KEY = "perks.preview.returnTo";
/** The tier being previewed, so `?as=` survives a link that drops the query. */
const TIER_KEY = "perks.preview.tier";

/** Patron routes. Preview means nothing anywhere else. */
const isPatronRoute = (pathname: string) =>
  pathname === "/tabs" ||
  pathname.startsWith("/tabs/") ||
  pathname === "/upgrade" ||
  pathname === "/account";

const session = {
  get(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      // Private browsing, or storage disabled.
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Preview degrades to "lost on navigation", which is where it started.
    }
  },
  clear(key: string) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Nothing to do.
    }
  },
};

export function PreviewProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tier = params.get("as");

  /*
   * The client seam learns the tier here rather than every call site passing
   * it, for the same reason the token is configured centrally: threading it
   * through would mean touching every screen, and a screen that forgot would
   * silently leave preview.
   *
   * Set during render rather than in an effect, so the value is already current
   * for any child that fetches on mount - React runs child effects before
   * parent effects, and the screens below are remounted by the key at the
   * bottom of this file.
   *
   * **Honesty about that claim:** moving this into an effect was tried and the
   * suite still passed, so the race it guards against is not demonstrated here.
   * It stays because the ordering guarantee is real and the assignment is free
   * and idempotent - not because a test proves it is load-bearing.
   */
  setPreviewTier(tier);

  // Leaving preview entirely is the only thing an effect is needed for.
  useEffect(() => () => setPreviewTier(null), []);

  /*
   * Keep `?as=` across navigation — FR-2.
   *
   * `<Link to={`/tabs/${slug}`}>` drops the query string, so clicking a card
   * while previewing left preview silently: the next screen answered as the
   * owner, who pledges nothing to their own campaign, and every asset 404'd.
   * Reported 2026-08-31.
   *
   * Fixed here rather than at each link. There are already four places that
   * navigate to a patron route and any new one would have to remember; a rule
   * enforced in one place cannot be forgotten in another. The URL stays the
   * state — this only puts back what a link dropped.
   */
  useEffect(() => {
    if (tier) {
      session.set(TIER_KEY, tier);
      return;
    }

    const remembered = session.get(TIER_KEY);
    if (remembered && isPatronRoute(location.pathname)) {
      const next = new URLSearchParams(params);
      next.set("as", remembered);
      setParams(next, { replace: true });
      return;
    }

    // Anywhere that is not a patron route means preview is over - reaching
    // Studio or the fork is a deliberate exit, not a dropped query string.
    if (!isPatronRoute(location.pathname)) session.clear(TIER_KEY);
  }, [tier, location.pathname, params, setParams]);

  // Remember where Studio was, so exiting returns there rather than guessing.
  useEffect(() => {
    if (!tier && location.pathname.startsWith("/studio")) {
      session.set(RETURN_KEY, location.pathname);
    }
  }, [tier, location.pathname]);

  const value = useMemo<PreviewState>(
    () => ({
      tier,
      previewing: tier !== null,
      setTier: (tierId: string) => {
        const next = new URLSearchParams(params);
        next.set("as", tierId);
        // `replace` so switching tier does not stack history entries the
        // author has to walk back through.
        setParams(next, { replace: true });
      },
      exit: () => {
        /*
         * Redundant today: `exit` navigates to a Studio route, and the effect
         * above clears the tier on any non-patron route. Kept because that is
         * a coincidence of where it navigates - if the remembered return route
         * ever became a patron one, leaving would silently keep preview on.
         */
        session.clear(TIER_KEY);
        navigate(session.get(RETURN_KEY) ?? "/studio");
      },
    }),
    [tier, params, setParams, navigate]
  );

  /*
   * Keyed on the tier, which remounts everything below on a switch.
   *
   * The previewed tier is part of the identity of every fetched thing on
   * screen: the same URL answers differently as 8 Bit than as 16 Bit. Changing
   * it therefore invalidates all of it, and a remount says exactly that.
   *
   * The alternative - adding the tier to every screen's dependency array - is a
   * rule each future screen has to remember, and the failure when one forgets
   * is silent: it shows the previous tier's library under the new tier's label,
   * which is the one outcome that could mislead someone checking a paywall.
   */
  return (
    <Context.Provider value={value}>
      <div key={tier ?? "real"} className={css.subtree}>
        {children}
      </div>
    </Context.Provider>
  );
}

export function usePreview(): PreviewState {
  const value = useContext(Context);
  if (!value) throw new Error("usePreview must be used inside <PreviewProvider>");
  return value;
}
